/**
 * Tools API Client
 *
 * API functions for fetching, validating, and testing tools
 * Migrated from tool-browser.js API calls
 */

import { apiClient } from './client';
import type {
  ToolsResponse,
  MCPToolsResponse,
  ToolValidationRequest,
  ToolValidationResponse,
} from '../../types/tools';

// Tool Testing Types
export interface ToolTestRequest {
  tool_name: string;
  parameters: Record<string, unknown>;
}

export interface ToolTestResponse {
  success: boolean;
  tool_name: string;
  result: unknown;
  error: string | null;
  error_type: string | null;
  execution_time_ms: number;
  parameters_used: Record<string, unknown>;
}

export interface ToolSchemaResponse {
  name: string;
  description: string;
  category: string | null;
  enabled: boolean;
  parameters: Record<string, unknown>;
  parameters_info: Record<string, {
    required: boolean;
    type?: string;
    default?: unknown;
  }>;
  is_async: boolean;
  requires_user_context: boolean;
}

/**
 * Fetch available custom and builtin tools
 * @param language - Language for display names ('cn' or 'en')
 */
export const fetchAvailableTools = async (
  language: 'cn' | 'en' = 'cn'
): Promise<ToolsResponse> => {
  const params = new URLSearchParams();
  params.append('language', language);

  const url = `/tools/available?${params.toString()}`;
  const response = await apiClient.get<ToolsResponse>(url);
  return response.data;
};

/**
 * Fetch available MCP servers and tools
 * @param forceReconnect - Force reconnect to MCP servers
 * @param language - Language for display names ('cn' or 'en')
 */
export const fetchAvailableMCPServers = async (
  forceReconnect = false,
  language: 'cn' | 'en' = 'cn'
): Promise<MCPToolsResponse> => {
  const params = new URLSearchParams();
  if (forceReconnect) {
    params.append('force_reconnect', 'true');
  }
  params.append('language', language);

  const url = `/tools/mcp/available?${params.toString()}`;
  const response = await apiClient.get<MCPToolsResponse>(url);
  return response.data;
};

/**
 * Validate tools configuration
 */
export const validateToolsConfig = async (
  config: ToolValidationRequest
): Promise<ToolValidationResponse> => {
  const response = await apiClient.post<ToolValidationResponse>(
    '/tools/validate',
    config
  );
  return response.data;
};

/**
 * Test a tool by invoking it with parameters
 */
export const testTool = async (
  request: ToolTestRequest
): Promise<ToolTestResponse> => {
  const response = await apiClient.post<ToolTestResponse>(
    '/tools/test',
    request
  );
  return response.data;
};

/**
 * Get the schema for a specific tool
 */
export const getToolSchema = async (
  toolName: string
): Promise<ToolSchemaResponse> => {
  const response = await apiClient.get<ToolSchemaResponse>(
    `/tools/schema/${encodeURIComponent(toolName)}`
  );
  return response.data;
};

export const toolsApi = {
  fetchAvailableTools,
  fetchAvailableMCPServers,
  validateToolsConfig,
  testTool,
  getToolSchema,
};
